import { t } from '@/utils/i18n'
import { useState } from 'react'
import { useMount, useUpdateEffect, useSetState, useUpdate } from 'ahooks'
import { useAliveController } from 'react-activation'
import {
  NavBar,
  Modal,
  InfiniteScroll,
  Toast,
  ErrorBlock,
  List,
  PullToRefresh,
} from 'antd-mobile'
import { DeleteOutline } from 'antd-mobile-icons'
import { useNavigate } from 'react-router-dom'
import { utils, apis, routesMap } from '@/utils'
import type { ProductItem as IProductItem, ProductDetail } from '@/types/product'
import { useCorpInfoStore, useCartStore } from '@/stores'
import FlexSearch from '@/components/FlexSearch'
import Tabbar from '@/components/Tabbar/Tabbar'
import eventUtil from '@/utils/event'
import FailProductItem from '../components/FailProductItem'
import ProductItem from '../components/ProductItem'
import SummaryBar from '../components/SummaryBar'
import styles from './index.module.less'

const { orderApi } = apis
const { to, cloneDeep, diffTwoList } = utils

function ShoppingCart() {
  const { corpInfo } = useCorpInfoStore()
  const { corpId } = corpInfo
  const { cart, setCart } = useCartStore()
  const { cartId, selectAll, productCount } = cart
  const updateView = useUpdate()
  const [pageInfo, setPageInfo] = useSetState({
    pageNum: 0,
    pageSize: 10,
    hasNextPage: true,
  })
  const navigate = useNavigate()
  const aliveController = useAliveController()
  aliveController.drop('ConfirmOrder')
  const [productList, setProductList] = useState<IProductItem[][]>([])
  const [failProductList, setFailProductList] = useState<IProductItem[][]>([])
  const [searchText, setSearchText] = useState('')
  const [isCheckAll, setIsCheckAll] = useState(Boolean(selectAll))
  const [isDataEmpty, setIsDataEmpty] = useState(false)
  const [saveLoading, setSaveLoading] = useState(false)
  // const pullKey = useMemo(() => {
  //   return `${pageInfo.pageNum}-${pageInfo.pageSize}`
  // }, [pageInfo.pageNum, searchText])

  useMount(() => {
    getCartInfo()
  })

  useUpdateEffect(() => {
    getProductList()
  }, [searchText])

  useUpdateEffect(() => {
    resetProductList()
    getCartInfo()
  }, [productCount])

  // 加载更多列表
  async function loadNextPage() {
    if (!pageInfo.hasNextPage) return

    const pageNum = pageInfo.pageNum + 1
    setPageInfo({
      pageNum,
    })
    return getProductList(pageNum)
  }

  // 重置列表
  function resetProductList() {
    setPageInfo({
      pageNum: 1,
      hasNextPage: true,
    })
    setIsDataEmpty(false)
    return getProductList(1)
  }

  // 获取货品列表
  async function getProductList(_pageNum?: number) {
    if (!cartId) {
      return
    }

    const pageNum = _pageNum || pageInfo.pageNum
    const [err, res] = await to(
      orderApi.queryCartProduct({
        ...pageInfo,
        pageNum,
        cartId,
        corpId,
        sortKey: 'createTime',
        sortDirection: 0,
        searchText,
      })
    )

    if (err || !res) {
      // 当前页加载出错时，需要回退页码以便下次可以继续加载该页
      if (pageNum > 1) {
        setPageInfo({
          pageNum: pageInfo.pageNum - 1,
        })
      }
      return
    }

    const resData = (res.data || []).map((item: IProductItem) => {
      const { cloudProduct } = item
      // 后端存在数据维护不正确的问题，起订单位的下标会出现大于等于单位数组的长度，需要重置下标为0
      if (
        cloudProduct.unitPriceArray &&
        cloudProduct.productMoqUnitIndex > 0 &&
        cloudProduct.productMoqUnitIndex >= cloudProduct.unitPriceArray?.length
      ) {
        cloudProduct.productMoqUnitIndex = 0
      }
      return item
    })
    
    // 筛选出正常和失效的商品
    const normalList =
      resData.filter(
        (item: IProductItem) => item.saleProduct.conflictStatus === 0
      ) || []
    const failList =
      resData.filter(
        (item: IProductItem) => item.saleProduct.conflictStatus !== 0
      ) || []
    if (pageNum === 1) {
      setProductList(normalList.length ? [normalList] : [[]])
      setFailProductList(failList.length ? [failList] : [[]])
      setIsDataEmpty(Boolean(!resData.length))
    } else if (resData.length) {
      const pageIndex = pageNum - 1
      productList[pageIndex] = normalList
      failProductList[pageIndex] = failList
      setProductList(productList)
      setFailProductList(failProductList)
    }
    setPageInfo({
      hasNextPage: pageNum < res.pages,
    })
    return resData
  }

  // 搜索变化
  function onSearchChange(value: string) {
    setSearchText(value)
    setPageInfo({ pageNum: 1 })
  }

  // 获取购物车信息
  async function getCartInfo() {
    const { accountId } = corpInfo
    if (!accountId) {
      return
    }

    const [err, res] = await to(
      orderApi.getCartInfo({
        customerAccountId: corpInfo.accountId,
        corpId,
        source: 2,
      })
    )

    if (err) {
      return
    }
    const { amount, productCount, quantity, selectAll } = res.data || {}
    setCart({
      ...cart,
      amount,
      productCount,
      quantity,
    })
    setIsCheckAll(Boolean(selectAll))
  }

  // 勾选货品
  function checkProduct(productData: IProductItem) {
    const selected = productData.saleProduct.selected
    if (!selected) {
      setIsCheckAll(false)
      // 如果勾选了，有可能为全选
    } else {
      const checkAll = diffTwoList(getAllSelectedIds(), getAllProductIds())
      setIsCheckAll(checkAll)
    }
    requestAddProd(productData)
    updateView()
  }

  // 删除货品
  async function deleteProd(pageIdx: number, productIdx: number) {
    const product = productList[pageIdx][productIdx] || {}
    const cloneProduct = cloneDeep(product)

    const saleProduct = cloneProduct.saleProduct || {}
    const productId = saleProduct.productId
    const [err, res] = await to(
      orderApi.deleteProd({
        corpId,
        cartId,
        saleProductIds: [saleProduct.saleProductId],
      })
    )
    if (err) {
      return
    }

    productList[pageIdx].splice(productIdx, 1)
    const resData = res.data
    setCart({
      ...cart,
      ...resData,
    })
    setProductList([...productList])

    eventUtil.emit('updateProduct', [{saleProduct: null, productId}])
  }

  // 更新货品数量
  async function updateProdQuantity(
    e: { quantity: number, detailId: string },
    pageIdx: number,
    productIdx: number
  ) {
    /**
     * detail: {
     *  defaultIndex: 选择的单位索引,
     *  quantity: 下单数量
     * }
     * */
    const { quantity, detailId } = e
    const product = productList[pageIdx][productIdx] || {}
    const saleProduct = product.saleProduct || {}
    const productId = saleProduct.productId

    if (quantity !== undefined) {
      const detailIndex = saleProduct.details.findIndex((item: ProductDetail) => item.saleDetailId === detailId)
      saleProduct.details[detailIndex].quantity = Number(quantity)

      // 当一个规格数量为0时，删除该规格
      if (Number(quantity) === 0) {
        saleProduct.details.splice(detailIndex, 1)
      }
      saleProduct.totalQty = saleProduct.details.reduce((a: number, b: ProductDetail) => a + b.quantity, 0)
      productList[pageIdx][productIdx] = product
      setProductList([...productList])

      // 当一个货品的所有规格数量都为0时，删除该货品
      if (saleProduct.details.every((detail: ProductDetail) => detail.quantity === 0)) {
        deleteProd(pageIdx, productIdx)
        return
      }

      const [err] = await requestAddProd(product)
      // 通知货品列表页更新数量
      if (!err) eventUtil.emit('updateProduct', [{saleProduct, productId}])
    }
  }

  // 请求添加/更新货品
  async function requestAddProd(product: IProductItem) {
    const saleProduct = product.saleProduct || {}
    saleProduct.conflictStatus = 0
    const params = {
      saleProduct,
    }

    const [err, res] = await to(orderApi.addUpdate(params))

    if (err) {
      // 请求失败了，重新刷新列表和购物车
      resetProductList()
      getCartInfo()
      return [err]
    }
    const resData = res.data || {}
    setCart({
      ...cart,
      ...resData,
    })

    return [err, res]
  }

  // 判断购物车是否为空
  function isCartEmpty() {
    return !productList || !productList?.length || !productList?.[0]?.length
  }

  // 清空失效商品
  async function clearFailProduct(isShowMsg = true) {
    const [err, res] = await to(orderApi.clearConflictProduct({
      corpId,
      cartId,
    }))
    if (err || !res) {
      return
    }

    const resData = res.data || {}
    const { leftProductCount } = resData
    if (isShowMsg) Toast.show(t('清空成功'))
    setFailProductList([])
    return leftProductCount
  }

  // 全选变化
  function checkAllChange(data: { isCheckAll: boolean }) {
    const { isCheckAll: _isCheckAll } = data
    setIsCheckAll(_isCheckAll)
    handleAllProductsSelect(_isCheckAll)
  }

  // 勾选全选变化后，处理每个商品的勾选
  function handleAllProductsSelect(isCheckAll: boolean) {
    productList.forEach((pageData: IProductItem[]) => {
      pageData.forEach((product: IProductItem) => {
        product.saleProduct.selected = isCheckAll
      })
    })
  }

  // 获取所有勾选的货品id
  function getAllSelectedIds() {
    return productList
      .flat()
      .filter((item) => item.saleProduct.selected)
      .map((item) => item.saleProduct?.productId)
  }

  // 获取所有的货品id
  function getAllProductIds() {
    return productList.flat().map((item) => item.saleProduct?.productId)
  }

  // 确认提交订单
  async function orderConfirm() {
    const allSelectedIds = getAllSelectedIds()
    if (!allSelectedIds.length) {
      Toast.show(t('请选择下单货品'))
      return
    }

    setSaveLoading(true)
    const { status, message } = await validateCart({
      cartId,
      checkType: 2, // 1首次进入购物车检测 2点击下单检测 3结账检测
      corpId,
    })
    setSaveLoading(false)
    // 0代表没有冲突
    if (status === 0) {
      navigate(routesMap.ConfirmOrder, {
        state: { fromPage: 'ShoppingCart' },
      })
    // 1代表有冲突
    } else if (status === 1) {
      Modal.confirm({
        title: '',
        content: message,
        confirmText: t('删除'),
        cancelText: t('取消'),
      }).then(async (res) => {
        // 点击删除
        if (res) {
          const leftProductCount = await clearFailProduct(false)
          // 如果还有正常的货品，才跳转去确认订单页
          if (leftProductCount > 0) {
            navigate(routesMap.ConfirmOrder)
          } else {
            resetProductList()
            getCartInfo()
          }
        // 点击取消
        } else {
          resetProductList()
          getCartInfo()
        }
        const IS_REFRESH = true // 是否刷新货品列表
        eventUtil.emit('updateProduct', [], IS_REFRESH)
      })
    // 其它情况，代表冲突校验报错
    } else {
      Toast.show(message)
      resetProductList()
    }
  }

  // 校验购物车
  function validateCart(data: {
    cartId: string
    corpId: string
    checkType: number
  }): Promise<{ status: number; message: string }> {
    return new Promise((resolve) => {
      orderApi
        .shoppingCartValidateWholeCart(data, { showMessage: false })
        .then(() => {
          resolve({ status: 0, message: '' })
        })
        .catch((err: any) => {
          if (err.code === 210060) {
            /*
             * deleteCount 删除或下架
             * infoChangeCount 商品档案变更
             * stockCount 库存不足
             */
            const { data } = err
            const { infoChangeCount, stockCount, deleteCount } = data
            // 有冲突
            const count = infoChangeCount || stockCount || deleteCount
            const message = t('{{deleteCount}}个货品已下架或库存不足，是否删除？', {
              deleteCount: count,
            })
            resolve({ status: 1, message })
          } else {
            resolve({ status: -1, message: err.msg })
          }
        })
    })
  }

  const NavRight = (
    <div className={styles['navbar-right']}>
      <FlexSearch
        value={searchText}
        width="calc(96% - 50px)"
        placeholder={ t('搜索') }
        onChange={onSearchChange}
      />
    </div>
  )

  return (
    <div className={styles['shopping-cart-container']}>
      <div
        className={styles['title-section']}
        style={{ position: 'relative', width: '100%' }}
      >
        <NavBar children={t('购物车')} back={null} right={NavRight} />
      </div>

      <div className={styles['list-section']}>
        <PullToRefresh onRefresh={ resetProductList }>
          <List>
            {!!productList.length &&
              productList.map((pageItem, pageIndex) => (
                <div key={pageIndex}>
                  {!!pageItem.length &&
                    pageItem.map((item, productIndex) => (
                      <ProductItem
                        key={item.saleProduct?.productId}
                        className={styles['product-item']}
                        productData={item}
                        onCheck={(data) => checkProduct(data)}
                        onDeleteProd={() => deleteProd(pageIndex, productIndex)}
                        onUpdateProdQuantity={(data) =>
                          updateProdQuantity(data, pageIndex, productIndex)
                        }
                      />
                    ))}
                </div>
              ))}

            {!!failProductList.flat().length && (
              <div className={styles['fail-product-section']}>
                <div className={styles['fail-product-header']}>
                  <div>
                    {failProductList.flat().length}
                    {t('件失效货品')}
                  </div>
                  <div
                    className={styles['fail-clear-btn']}
                    onClick={() => clearFailProduct(true)}
                  >
                    <DeleteOutline fontSize={16} color="#7F7F7F" />
                    <span className={styles['fail-clear-text']}>{t('清空')}</span>
                  </div>
                </div>

                {!!failProductList.length &&
                  failProductList.map((pageItem, pageIndex) => (
                    <div key={pageIndex}>
                      {!!pageItem.length &&
                        pageItem.map((item) => (
                          <FailProductItem
                            key={item.saleProduct?.productId}
                            className={styles['product-item']}
                            productData={item}
                          />
                        ))}
                    </div>
                  ))}
              </div>
            )}
          </List>
        </PullToRefresh>

        {!isDataEmpty && (
          <InfiniteScroll
            hasMore={pageInfo.hasNextPage}
            finished-text={t('没有更多了')}
            loadMore={loadNextPage}
          />
        )}

        {isDataEmpty && (
          <ErrorBlock
            status="empty"
            fullPage
            title={t('购物车还是空的呐～')}
            description=""
          />
        )}
      </div>

      {!isCartEmpty() && (
        <SummaryBar
          className={styles['summary-bar']}
          corpId={corpId}
          isCheckAll={isCheckAll}
          loading={saveLoading}
          onCheckAll={checkAllChange}
          onOrderConfirm={orderConfirm}
        />
      )}

      <Tabbar />
    </div>
  )
}

export default ShoppingCart
